/*
 * Purpose: Workflow scheduler — tenant-persisted schedules + in-app runner
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import { APP_ROUTES } from '@/lib/appRoutes';
import { Link } from 'react-router-dom';
import {
  loadSchedules,
  saveSchedules,
  type ScheduleFrequency,
  type WorkflowSchedule,
} from '@/lib/workflowSchedules';

const frequencyLabels: Record<ScheduleFrequency, string> = {
  once: 'Once',
  hourly: 'Every Hour',
  daily: 'Daily',
  weekly: 'Weekly (Mon)',
  monthly: 'Monthly (1st)',
};

export function WorkflowScheduler() {
  const workflows = useWorkflowStore((s) => s.workflows);
  const { canUse, message } = useSubscriptionGate();
  const canSchedule = canUse('scheduled_flows');
  const [schedules, setSchedules] = useState<WorkflowSchedule[]>(() => loadSchedules());
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    workflowId: '',
    frequency: 'daily' as ScheduleFrequency,
    time: '09:00',
  });

  useEffect(() => {
    saveSchedules(schedules);
  }, [schedules]);

  const persist = (next: WorkflowSchedule[]) => {
    setSchedules(next);
    saveSchedules(next);
  };

  const handleAddSchedule = () => {
    const workflow = workflows.find((w) => w.id === formData.workflowId);
    if (!workflow || !formData.time) {
      toast.error('Select a flow and time');
      return;
    }

    const newSchedule: WorkflowSchedule = {
      id: Math.random().toString(36).slice(2, 11),
      workflowId: workflow.id,
      workflowName: workflow.name,
      frequency: formData.frequency,
      time: formData.time,
      enabled: true,
    };

    persist([...schedules, newSchedule]);
    setFormData({ workflowId: '', frequency: 'daily', time: '09:00' });
    setIsOpen(false);
    toast.success('Schedule saved — runs while Autoblocks is open');
  };

  const handleDeleteSchedule = (id: string) => {
    persist(schedules.filter((s) => s.id !== id));
    toast.success('Schedule deleted');
  };

  const handleToggleSchedule = (id: string) => {
    persist(schedules.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  };

  if (!canSchedule) {
    return (
      <Card className="p-6 text-center space-y-3">
        <Clock className="w-10 h-10 text-muted-foreground/40 mx-auto" />
        <p className="text-sm text-muted-foreground">{message('scheduled_flows')}</p>
        <Button actionTier="secondary" size="sm" asChild>
          <Link to={APP_ROUTES.pricing}>View plans</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Workflow Scheduler</h3>
          <p className="text-sm text-muted-foreground">
            Schedules fire while this workspace is open in the browser.
          </p>
        </div>
        <Button size="sm" onClick={() => setIsOpen(true)} disabled={workflows.length === 0}>
          <Plus className="w-4 h-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {schedules.length === 0 ? (
        <Card className="p-6 text-center">
          <Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">No schedules yet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {schedules.map((schedule) => (
            <Card
              key={schedule.id}
              className={`p-4 ${schedule.enabled ? 'border-green-500/20 bg-green-500/5' : 'opacity-50'}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-semibold text-foreground">{schedule.workflowName}</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {frequencyLabels[schedule.frequency]} at {schedule.time}
                    {schedule.lastRunAt && (
                      <span className="ml-2">· Last {new Date(schedule.lastRunAt).toLocaleString()}</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleToggleSchedule(schedule.id)}>
                    {schedule.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleDeleteSchedule(schedule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Schedule</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Flow</Label>
              <Select
                value={formData.workflowId}
                onValueChange={(workflowId) => setFormData({ ...formData, workflowId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a saved flow" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Frequency</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value: ScheduleFrequency) =>
                  setFormData({ ...formData, frequency: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="hourly">Every Hour</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly (Monday)</SelectItem>
                  <SelectItem value="monthly">Monthly (1st)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="schedule-time">Time</Label>
              <input
                id="schedule-time"
                type="time"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSchedule}>Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
